import toast from 'react-hot-toast';

import { fetchWithRetry, isAbortError, isRetryableHttpStatus, type RetryAttemptInfo } from './retry';

export {
  fetchWithRetry,
  isAbortError,
  isRetryableHttpStatus,
  shouldRetryRequest,
  type FetchWithRetryOptions,
  type RetryAttemptInfo,
} from './retry';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const DEV_API_KEY = import.meta.env.VITE_DEV_API_KEY as string | undefined;

function devAuthHeaders(): Record<string, string> {
  if (import.meta.env.DEV && DEV_API_KEY) {
    return { 'X-API-Key': DEV_API_KEY };
  }
  return {};
}

export interface ApiError {
  status: number;
  statusText: string;
  body: unknown;
  message: string;
}

export interface ApiResponse<T> {
  ok: boolean;
  data?: T;
  error?: ApiError;
  status: number;
}

export interface HealthCheck {
  status: string;
  service: string;
  version: string;
  timestamp?: string;
  environment?: string | null;
}

export interface UserProfile {
  sub: string;
  email: string;
  name: string;
  preferred_username?: string;
  groups?: string[];
}

export interface IntegrationStatus {
  name: string;
  status: 'ok' | 'warn' | 'error' | 'unknown';
  lastSyncAt?: string;
  message?: string;
  configured?: boolean;
}

export interface DashboardAttentionItem {
  id: string;
  severity: 'info' | 'warning' | 'danger' | 'success';
  title: string;
  action: string;
  href: string;
}

export interface DashboardSummary {
  apiStatus: 'healthy' | 'degraded';
  syncStatus: 'healthy' | 'stale' | 'failing';
  lastSyncAt?: string;
  missingReceipts: number;
  failedWebhooks: number;
  unpaidInvoices: number;
  unpaidInvoicesConfigured: boolean;
  taxDeadlines: number;
  backupHealth: 'healthy' | 'stale' | 'failing';
  pendingApprovals: number;
  integrations: IntegrationStatus[];
  attentionItems: DashboardAttentionItem[];
}

export interface ApprovalItem {
  id: string;
  type:
    | 'tax_filing'
    | 'crypto_transfer'
    | 'destructive_action'
    | 'retention_change'
    | 'gateway_settlement'
    | 'uncertain_match';
  title: string;
  description: string;
  requestedAt: string;
  requestedBy: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  metadata?: Record<string, unknown>;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AiChatResponse {
  id?: string;
  model?: string;
  content: string;
  usage?: Record<string, unknown>;
  finish_reason?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

/** True when a response body is an HTML error page (nginx/Traefik/etc.), not API JSON. */
export function looksLikeHtmlErrorPage(text: string): boolean {
  const sample = text.slice(0, 800).toLowerCase();
  return (
    /<\s*html[\s>]/.test(sample) ||
    /<\s*!doctype\s+html/.test(sample) ||
    (/nginx\//.test(sample) && /<\s*(center|h1|title)/.test(sample))
  );
}

/**
 * Convert raw HTTP error bodies into a short operator-facing message.
 * Prevents nginx/Traefik HTML 404/502 pages from appearing as chat content.
 */
export function summarizeHttpErrorBody(
  status: number,
  text: string,
  statusText = ''
): string {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return statusText ? `${status} ${statusText}` : `Request failed (${status || 'network'})`;
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const nested =
      parsed && typeof parsed === 'object' && parsed.error && typeof parsed.error === 'object'
        ? (parsed.error as Record<string, unknown>).message
        : undefined;
    const detail = parsed.detail;
    const message =
      (typeof nested === 'string' && nested) ||
      (typeof parsed.message === 'string' && parsed.message) ||
      (typeof detail === 'string' && detail) ||
      null;
    if (message) {
      return looksLikeHtmlErrorPage(message)
        ? summarizeHttpErrorBody(status, message, statusText)
        : message.length > 400
          ? `${message.slice(0, 400)}…`
          : message;
    }
  } catch {
    // not JSON
  }
  if (looksLikeHtmlErrorPage(trimmed)) {
    const title =
      trimmed.match(/<title>\s*([^<]+?)\s*<\/title>/i)?.[1]?.trim() ||
      trimmed.match(/<h1>\s*([^<]+?)\s*<\/h1>/i)?.[1]?.trim() ||
      `${status || 'error'} from proxy`;
    return `Service temporarily unavailable (${title}). The API or proxy may be restarting — use Resume.`;
  }
  return trimmed.length > 400 ? `${trimmed.slice(0, 400)}…` : trimmed;
}

function buildApiError(response: Response, body: unknown): ApiError {
  const fromObject =
    (typeof body === 'object' && body && 'error' in body && typeof body.error === 'object' && body.error && 'message' in body.error && typeof body.error.message === 'string'
      ? body.error.message
      : undefined) ??
    (typeof body === 'object' && body && 'message' in body && typeof body.message === 'string'
      ? body.message
      : undefined);

  let message: string;
  if (typeof body === 'string') {
    message = summarizeHttpErrorBody(response.status, body, response.statusText);
  } else if (fromObject) {
    message = looksLikeHtmlErrorPage(fromObject)
      ? summarizeHttpErrorBody(response.status, fromObject, response.statusText)
      : fromObject;
  } else {
    message = `${response.status} ${response.statusText}`;
  }

  return {
    status: response.status,
    statusText: response.statusText,
    body,
    message,
  };
}

export interface ApiRequestOptions {
  suppressToast?: boolean;
  /** Total attempts including the first. Default 1 (no retry). */
  retries?: number;
  baseDelayMs?: number;
  onRetry?: (info: RetryAttemptInfo) => void;
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...devAuthHeaders(),
  };

  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const retries = Math.max(1, options.retries ?? 1);

  try {
    const response = await fetchWithRetry(
      url,
      {
        method,
        credentials: 'include',
        headers,
        body:
          body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      },
      {
        retries,
        baseDelayMs: options.baseDelayMs ?? 500,
        onRetry: options.onRetry,
      }
    );

    const responseBody = await parseJson(response);

    if (!response.ok) {
      const error = buildApiError(response, responseBody);
      if (isRetryableHttpStatus(response.status) && retries > 1) {
        error.message = `${error.message} — still unavailable after ${retries} attempts. Please try again in a moment.`;
      }
      if (!options.suppressToast) {
        toast.error(error.message);
      }
      return { ok: false, error, status: response.status };
    }

    return { ok: true, data: responseBody as T, status: response.status };
  } catch (err) {
    const message =
      retries > 1
        ? `${err instanceof Error ? err.message : 'Network error'} — still unavailable after ${retries} attempts. Please try again in a moment.`
        : err instanceof Error
          ? err.message
          : 'Network error';
    const error: ApiError = {
      status: 0,
      statusText: 'Network Error',
      body: err,
      message,
    };
    if (!options.suppressToast) {
      toast.error(message);
    }
    return { ok: false, error, status: 0 };
  }
}

export function get<T>(path: string, options?: ApiRequestOptions) {
  return apiRequest<T>('GET', path, undefined, options);
}

export function post<T>(path: string, body?: unknown, options?: ApiRequestOptions) {
  return apiRequest<T>('POST', path, body, options);
}

export function patch<T>(path: string, body?: unknown, options?: ApiRequestOptions) {
  return apiRequest<T>('PATCH', path, body, options);
}

export function put<T>(path: string, body?: unknown, options?: ApiRequestOptions) {
  return apiRequest<T>('PUT', path, body, options);
}

export function del<T>(path: string, options?: ApiRequestOptions) {
  return apiRequest<T>('DELETE', path, undefined, options);
}

export async function fetchHealth(): Promise<ApiResponse<HealthCheck>> {
  return get<HealthCheck>('/v1/health', { suppressToast: true });
}

export async function fetchMe(): Promise<ApiResponse<UserProfile>> {
  return get<UserProfile>('/v1/me', { suppressToast: true });
}

export async function fetchDashboardSummary(): Promise<ApiResponse<DashboardSummary>> {
  return get<DashboardSummary>('/v1/dashboard/summary', { suppressToast: true });
}

export interface AiInsight {
  configured: boolean;
  content?: string | null;
  model?: string | null;
  provider?: string | null;
  generatedAt?: string | null;
  message?: string | null;
}

export async function fetchAiInsights(): Promise<ApiResponse<AiInsight>> {
  return get<AiInsight>('/v1/ai/insights', { suppressToast: true });
}

export interface AppLinkApiItem {
  id: string;
  name: string;
  url: string;
  description?: string;
  integrationKey?: string;
}

export interface AppLinksApiResponse {
  links: AppLinkApiItem[];
  urls: Record<string, string>;
}

export async function fetchAppLinks(): Promise<ApiResponse<AppLinksApiResponse>> {
  return get<AppLinksApiResponse>('/v1/apps/links', { suppressToast: true });
}

// Charts
export type ChartPeriod = '30d' | '90d' | '12m';

export interface OverviewChartsResponse {
  status: string;
  period: ChartPeriod;
  revenueExpenses: {
    source: string;
    configured: boolean;
    data: Array<{ period: string; revenue: number; expenses: number }>;
  };
  cashFlow: {
    source: string;
    configured: boolean;
    data: Array<{ date: string; net: number; cumulative: number }>;
  };
  integrationHealth: { connected: number; warning: number; error: number };
  recentActivity: Array<{
    id: number;
    timestamp: string | null;
    action: string;
    actor: string;
    status: string | null;
    resourceType: string | null;
  }>;
  attention: {
    failedWebhooks: number;
    unpaidInvoices: number;
    pendingApprovals: number;
  };
}

export interface TransactionsChartResponse {
  status: string;
  source: string;
  configured: boolean;
  message?: string;
  data: Array<{ category: string; amount: number }>;
  total: number;
}

export interface PaymentsChartResponse {
  status: string;
  source: string;
  configured: boolean;
  message?: string;
  data: Array<{ method: string; count: number; volume: number }>;
}

export interface TaxChartResponse {
  status: string;
  year: number;
  source: string;
  configured: boolean;
  completed: number;
  total: number;
  data: Array<{
    quarter: string;
    status: string;
    label: string;
    formType: string | null;
  }>;
}

export async function fetchOverviewCharts(
  period: ChartPeriod = '90d'
): Promise<ApiResponse<OverviewChartsResponse>> {
  return get<OverviewChartsResponse>(`/v1/dashboard/charts/overview${buildQuery({ period })}`, {
    suppressToast: true,
  });
}

export async function fetchTransactionsChart(): Promise<ApiResponse<TransactionsChartResponse>> {
  return get<TransactionsChartResponse>('/v1/dashboard/charts/transactions', { suppressToast: true });
}

export async function fetchPaymentsChart(): Promise<ApiResponse<PaymentsChartResponse>> {
  return get<PaymentsChartResponse>('/v1/dashboard/charts/payments', { suppressToast: true });
}

export async function fetchTaxChart(year?: number): Promise<ApiResponse<TaxChartResponse>> {
  return get<TaxChartResponse>(`/v1/dashboard/charts/tax${buildQuery({ year })}`, {
    suppressToast: true,
  });
}

export interface RawIntegrationStatus {
  name: string;
  status: string;
  configured: boolean;
  connected: boolean;
  last_checked: string;
  last_error?: string | null;
  retry_accepted?: boolean;
}

export const INTEGRATION_KEYS = [
  'erpnext',
  'twenty',
  'solidinvoice',
  'documenso',
  'paperless',
  'ocis',
  'mercury',
  'paypal',
  'stripe',
  'chain2pay',
  'paymento',
  'monero',
  'n8n',
  'btcpay',
] as const;

export type IntegrationKey = (typeof INTEGRATION_KEYS)[number];

export const INTEGRATION_LABELS: Record<string, string> = {
  erpnext: 'ERPNext',
  twenty: 'Twenty CRM',
  solidinvoice: 'SolidInvoice',
  documenso: 'Documenso',
  paperless: 'Paperless',
  ocis: 'oCIS',
  mercury: 'Mercury',
  paypal: 'PayPal',
  stripe: 'Stripe',
  chain2pay: 'Chain2Pay',
  paymento: 'Paymento',
  monero: 'Monero',
  n8n: 'n8n Flows',
  btcpay: 'BTCPay',
};

export function integrationLabel(key: string): string {
  return INTEGRATION_LABELS[key] ?? key;
}

export function normalizeIntegrationStatusValue(raw: string): IntegrationStatus['status'] {
  const s = (raw ?? '').toLowerCase();
  if (s === 'ok' || s === 'healthy') return 'ok';
  if (s === 'warn' || s === 'degraded' || s === 'not_configured' || s === 'stale') return 'warn';
  if (s === 'error' || s === 'failing' || s === 'disconnected') return 'error';
  return 'unknown';
}

export async function fetchIntegrationStatus(
  name: string
): Promise<ApiResponse<RawIntegrationStatus>> {
  return get<RawIntegrationStatus>(`/v1/integrations/${encodeURIComponent(name)}/status`, {
    suppressToast: true,
  });
}

export async function retryIntegration(name: string): Promise<ApiResponse<RawIntegrationStatus>> {
  return post<RawIntegrationStatus>(
    `/v1/integrations/${encodeURIComponent(name)}/retry`,
    undefined,
    { suppressToast: false }
  );
}

export async function fetchApprovals(): Promise<ApiResponse<PaginatedResponse<ApprovalItem>>> {
  return get<PaginatedResponse<ApprovalItem>>('/v1/approvals', { suppressToast: true });
}

export async function approveApproval(id: string): Promise<ApiResponse<ApprovalItem>> {
  return post<ApprovalItem>(`/v1/approvals/${encodeURIComponent(id)}/approve`, undefined, {
    suppressToast: false,
  });
}

export async function rejectApproval(id: string): Promise<ApiResponse<ApprovalItem>> {
  return post<ApprovalItem>(`/v1/approvals/${encodeURIComponent(id)}/reject`, undefined, {
    suppressToast: false,
  });
}

export async function sendAiChat(
  messages: ChatMessage[],
  options: { template?: string; context?: Record<string, unknown>; stream?: boolean } = {}
): Promise<ApiResponse<AiChatResponse>> {
  return post<AiChatResponse>(
    '/v1/ai/chat',
    { messages, template: options.template, context: options.context, stream: false },
    { suppressToast: false }
  );
}

export async function stageAiAction(payload: {
  action_type: string;
  title: string;
  description: string;
  risk_level?: string;
  payload?: Record<string, unknown>;
}): Promise<ApiResponse<{ ok: boolean; approvalId: string; status: string; message: string }>> {
  return post('/v1/ai/actions/stage', payload, { suppressToast: false });
}

export async function refreshAuth(): Promise<ApiResponse<{ status: string }>> {
  return post<{ status: string }>('/v1/auth/refresh', undefined, { suppressToast: false });
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// Mercury
export interface MercurySyncRequest {
  account_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface MercurySyncResponse {
  status: string;
  synced?: number;
  reconciled?: number;
  account_count?: number;
}

export interface IntegrationTransactionListResponse {
  status: string;
  data: Record<string, unknown>[];
  total: number;
}

export async function syncMercury(
  payload: MercurySyncRequest = {}
): Promise<ApiResponse<MercurySyncResponse>> {
  return post<MercurySyncResponse>('/v1/mercury/sync', payload);
}

export async function fetchMercuryTransactions(
  accountId?: string,
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/mercury/transactions${buildQuery({ account_id: accountId, limit, offset })}`
  );
}

// PayPal
export interface PayPalSyncRequest {
  start_date?: string;
  end_date?: string;
}

export interface PayPalInvoiceCreateRequest {
  recipient_email: string;
  amount: string;
  currency?: string;
  note?: string;
  due_date?: string;
}

export interface PayPalRefundRequest {
  amount?: string;
  currency?: string;
  reason?: string;
}

export async function syncPayPalTransactions(
  payload: PayPalSyncRequest = {}
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/paypal/transactions/sync', payload);
}

export async function fetchPayPalTransactions(
  startDate?: string,
  endDate?: string,
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/paypal/transactions${buildQuery({ start_date: startDate, end_date: endDate, limit, offset })}`
  );
}

export async function createPayPalInvoice(
  payload: PayPalInvoiceCreateRequest
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/paypal/invoices', payload);
}

export async function sendPayPalInvoice(
  invoiceId: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>(`/v1/paypal/invoices/${encodeURIComponent(invoiceId)}/send`);
}

export async function getPayPalInvoiceStatus(
  invoiceId: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return get<Record<string, unknown>>(`/v1/paypal/invoices/${encodeURIComponent(invoiceId)}`);
}

export async function fetchPayPalOrders(
  status = 'all',
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/paypal/orders${buildQuery({ status, limit, offset })}`
  );
}

export async function capturePayPalOrder(
  orderId: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>(`/v1/paypal/orders/${encodeURIComponent(orderId)}/capture`);
}

export async function refundPayPalPayment(
  paymentId: string,
  payload: PayPalRefundRequest = {}
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>(
    `/v1/paypal/payments/${encodeURIComponent(paymentId)}/refund`,
    payload
  );
}

// Chain2Pay
export interface Chain2PaySyncRequest {
  start_date?: string;
  end_date?: string;
}

export interface Chain2PayReconcileRequest {
  payment_id: string;
  invoice_id: string;
}

export interface Chain2PayStatusResponse {
  status: string;
  configured: boolean;
  connected: boolean;
  last_error?: string | null;
}

export async function syncChain2PayPayments(
  payload: Chain2PaySyncRequest = {}
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/chain2pay/payments/sync', payload);
}

export async function fetchChain2PayPayments(
  status = 'all',
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/chain2pay/payments${buildQuery({ status, limit, offset })}`
  );
}

export async function fetchChain2PayPaymentStatus(
  paymentId: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return get<Record<string, unknown>>(`/v1/chain2pay/payments/${encodeURIComponent(paymentId)}`);
}

export async function fetchChain2PayPayouts(
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/chain2pay/payouts${buildQuery({ limit, offset })}`
  );
}

export async function reconcileChain2PayInvoice(
  payload: Chain2PayReconcileRequest
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/chain2pay/invoices/reconcile', payload);
}

export async function fetchChain2PayStatus(): Promise<ApiResponse<Chain2PayStatusResponse>> {
  return get<Chain2PayStatusResponse>('/v1/chain2pay/status');
}

export interface PaymentoStatusResponse {
  status: string;
  configured: boolean;
  connected: boolean;
  last_error?: string | null;
  receive_addresses?: Record<string, string>;
}

export interface PaymentoCreateRequest {
  fiat_amount?: string;
  fiat_currency?: string;
  order_id?: string;
  return_url?: string;
  speed?: number;
}

export async function fetchPaymentoStatus(): Promise<ApiResponse<PaymentoStatusResponse>> {
  return get<PaymentoStatusResponse>('/v1/paymento/status');
}

export async function fetchPaymentoPayments(
  limit = 50,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/paymento/payments${buildQuery({ limit, offset })}`
  );
}

export async function fetchPaymentoAddresses(): Promise<ApiResponse<{ addresses: Record<string, string> }>> {
  return get<{ addresses: Record<string, string> }>('/v1/paymento/addresses');
}

export async function createPaymentoPayment(
  payload: PaymentoCreateRequest
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/paymento/payments/request', payload);
}

export async function verifyPaymentoPayment(
  token: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/paymento/payments/verify', { token });
}

export interface StripeStatusResponse {
  status: string;
  configured: boolean;
  connected: boolean;
  last_error?: string | null;
  publishable_key_configured?: boolean;
  publishable_key?: string | null;
  mode?: string;
  webhook_configured?: boolean;
}

export interface StripePaymentCreateRequest {
  amount: string;
  currency?: string;
  description?: string;
  invoice_ref?: string;
}

export async function fetchStripeStatus(): Promise<ApiResponse<StripeStatusResponse>> {
  return get<StripeStatusResponse>('/v1/stripe/status');
}

export async function fetchStripePayments(
  limit = 50,
  startingAfter?: string
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/stripe/payments${buildQuery({ limit, starting_after: startingAfter })}`
  );
}

export async function fetchStripeCustomers(
  limit = 50,
  startingAfter?: string
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/stripe/customers${buildQuery({ limit, starting_after: startingAfter })}`
  );
}

export async function fetchStripeBalance(): Promise<ApiResponse<Record<string, unknown>>> {
  return get<Record<string, unknown>>('/v1/stripe/balance');
}

export async function fetchStripeWebhookEvents(
  limit = 20,
  offset = 0
): Promise<ApiResponse<IntegrationTransactionListResponse>> {
  return get<IntegrationTransactionListResponse>(
    `/v1/stripe/webhook-events${buildQuery({ limit, offset })}`
  );
}

export async function createStripePaymentLink(
  payload: StripePaymentCreateRequest
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/stripe/payment-links', payload);
}

export async function createStripePaymentIntent(
  payload: StripePaymentCreateRequest
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>('/v1/stripe/payment-intents', payload);
}

// Search
export interface WebSearchRequest {
  query: string;
  sources?: string[];
  source?: string;
  limit?: number;
}

export interface WebSearchResponse {
  data: unknown;
}

export async function searchWeb(
  payload: WebSearchRequest
): Promise<ApiResponse<WebSearchResponse>> {
  return post<WebSearchResponse>('/v1/search/web', payload);
}

export type UnifiedSearchEntityType = 'invoice' | 'client' | 'document' | 'contract' | 'payment';

export interface UnifiedSearchResult {
  id: string;
  type: UnifiedSearchEntityType;
  title: string;
  subtitle?: string | null;
  url?: string | null;
  score: number;
  metadata?: Record<string, unknown>;
}

export interface UnifiedSearchData {
  query: string;
  types: UnifiedSearchEntityType[];
  results: UnifiedSearchResult[];
  total: number;
  limit: number;
  offset: number;
}

export interface UnifiedSearchResponse {
  data: UnifiedSearchData;
}

export interface UnifiedSearchParams {
  q: string;
  type?: string;
  limit?: number;
  offset?: number;
}

export async function searchUnified(
  params: UnifiedSearchParams
): Promise<ApiResponse<UnifiedSearchResponse>> {
  return get<UnifiedSearchResponse>(`/v1/search${buildQuery({ ...params })}`, {
    suppressToast: true,
  });
}

// Transactions
export interface TransactionRow {
  id: string;
  source: string;
  amount: number | string | null;
  currency: string;
  description: string | null;
  counterparty: string | null;
  reference: string | null;
  date: string | null;
  status: string;
  raw?: Record<string, unknown>;
}

export interface TransactionSearchResponse {
  status: string;
  data: TransactionRow[];
  total: number;
}

export interface TransactionSearchParams {
  query?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export async function searchTransactions(
  params: TransactionSearchParams = {}
): Promise<ApiResponse<TransactionSearchResponse>> {
  return get<TransactionSearchResponse>(`/v1/transactions/search${buildQuery({ ...params })}`, {
    suppressToast: true,
  });
}

// CRM
export interface CrmListResponse {
  data: Record<string, unknown>[];
}

export async function fetchCrmCompanies(limit = 50): Promise<ApiResponse<CrmListResponse>> {
  return get<CrmListResponse>(`/v1/crm/companies${buildQuery({ limit })}`, { suppressToast: true });
}

export async function fetchCrmContacts(limit = 50): Promise<ApiResponse<CrmListResponse>> {
  return get<CrmListResponse>(`/v1/crm/contacts${buildQuery({ limit })}`, { suppressToast: true });
}

export async function fetchCrmDeals(limit = 50): Promise<ApiResponse<CrmListResponse>> {
  return get<CrmListResponse>(`/v1/crm/deals${buildQuery({ limit })}`, { suppressToast: true });
}

// Invoices
export interface InvoiceListResponse {
  data: unknown;
}

export interface UnpaidInvoicesResponse {
  status: string;
  data: Record<string, unknown>[];
  total: number;
}

export interface InvoiceCreateRequest {
  client_id: string;
  amount?: string;
  currency?: string;
  description?: string;
  terms?: string;
  public_notes?: string;
  due_date?: string;
  line_items?: Record<string, unknown>[];
}

export async function fetchInvoices(limit = 20): Promise<ApiResponse<InvoiceListResponse>> {
  return get<InvoiceListResponse>(`/v1/invoices${buildQuery({ limit })}`, { suppressToast: true });
}

export async function fetchUnpaidInvoices(
  clientId?: string,
  limit = 50,
  offset = 0
): Promise<ApiResponse<UnpaidInvoicesResponse>> {
  return get<UnpaidInvoicesResponse>(
    `/v1/invoices/unpaid${buildQuery({ client_id: clientId, limit, offset })}`,
    { suppressToast: true }
  );
}

export async function createInvoice(
  payload: InvoiceCreateRequest
): Promise<ApiResponse<InvoiceListResponse>> {
  return post<InvoiceListResponse>('/v1/invoices', payload);
}

export interface InvoicePayLinkResponse {
  status: string;
  invoice_id: string;
  amount?: string;
  currency?: string;
  pay_url?: string | null;
  checkout_url?: string | null;
  stripe_status?: string | null;
  error?: string;
}

export async function createInvoicePayLink(
  invoiceId: string,
  options?: { method?: 'checkout' | 'stripe'; currency?: string }
): Promise<ApiResponse<InvoicePayLinkResponse>> {
  return post<InvoicePayLinkResponse>(`/v1/invoices/${encodeURIComponent(invoiceId)}/pay-link`, {
    method: options?.method ?? 'checkout',
    currency: options?.currency,
  });
}

export interface EntityDeadline {
  id: number;
  formId?: string | null;
  title: string;
  dueDate: string;
  status: string;
  sourceRule?: string;
  entityType?: string | null;
}

export async function fetchEntityDeadlines(
  withinDays = 90
): Promise<ApiResponse<{ ok: boolean; deadlines: EntityDeadline[]; count: number }>> {
  return get(`/v1/entity-memory/deadlines${buildQuery({ within_days: withinDays })}`, {
    suppressToast: true,
  });
}

export async function computeEntityDeadlines(
  forceRefresh = false
): Promise<ApiResponse<{ ok: boolean; deadlines: EntityDeadline[]; count: number }>> {
  return post('/v1/entity-memory/deadlines/compute', { forceRefresh });
}

export async function getInvoiceStatus(
  id: string
): Promise<ApiResponse<{ id: string; status: string }>> {
  return get<{ id: string; status: string }>(`/v1/invoices/${encodeURIComponent(id)}/status`);
}

// Contracts
export interface ContractFindResponse {
  status: string;
  data: Record<string, unknown>[];
  total: number;
}

export interface SendContractRequest {
  recipient_email?: string;
  message?: string;
}

export interface SendContractResponse {
  document_id: string;
  sent: boolean;
}

export async function findContracts(
  query?: string,
  status = 'all',
  limit = 50
): Promise<ApiResponse<ContractFindResponse>> {
  return get<ContractFindResponse>(
    `/v1/contracts/find${buildQuery({ query, status, limit })}`,
    { suppressToast: true }
  );
}

export async function sendContract(
  id: string,
  payload: SendContractRequest = {}
): Promise<ApiResponse<SendContractResponse>> {
  return post<SendContractResponse>(`/v1/contracts/${encodeURIComponent(id)}/send`, payload);
}

// Documents / Receipts
export interface ReceiptUploadRequest {
  file_name: string;
  content_base64: string;
  document_date?: string;
  amount?: string;
  currency?: string;
  tags?: string[];
}

export interface DocumentUploadResponse {
  id: number | string | null;
  status: string;
  ocis_mirrored?: boolean;
  ocrId?: string;
  documentType?: string;
  destination?: string;
  ocrProvider?: string;
  ocrStatus?: string;
  ocrTextPreview?: string;
}

export interface DocumentOcrResponse {
  id: string;
  fileHash: string;
  filename: string;
  text: string;
  provider?: string | null;
  confidence?: number | null;
  language?: string | null;
  pages: Array<Record<string, unknown>>;
  documentType?: string | null;
  destination?: string | null;
  storageRef?: string | null;
  paperlessId?: number | null;
  status: string;
  createdAt?: string | null;
}

export interface DocumentSearchResultItem {
  title: string;
  url?: string | null;
  snippet?: string | null;
  source: string;
  score: number;
  metadata: Record<string, unknown>;
}

export interface DocumentSearchData {
  query: string;
  paperless: Record<string, unknown>[];
  ocis: Record<string, unknown>[];
  results: DocumentSearchResultItem[];
  total: number;
}

export interface DocumentSearchResponse {
  data: DocumentSearchData;
}

export async function uploadReceipt(
  payload: ReceiptUploadRequest
): Promise<ApiResponse<DocumentUploadResponse>> {
  return post<DocumentUploadResponse>('/v1/documents/receipts', payload);
}

export async function searchDocuments(
  query: string,
  limit = 20
): Promise<ApiResponse<DocumentSearchResponse>> {
  return get<DocumentSearchResponse>(`/v1/documents/search${buildQuery({ q: query, limit })}`, {
    suppressToast: true,
  });
}

export async function uploadDocument(
  file: File
): Promise<ApiResponse<DocumentUploadResponse>> {
  const form = new FormData();
  form.append('file', file);
  return post<DocumentUploadResponse>('/v1/documents/upload', form);
}

export async function getDocumentOcr(
  ocrId: string
): Promise<ApiResponse<DocumentOcrResponse>> {
  return get<DocumentOcrResponse>(`/v1/documents/ocr/${encodeURIComponent(ocrId)}`, {
    suppressToast: true,
  });
}

// Files (oCIS)
export interface FileListItem {
  name: string;
  type?: string | null;
  size?: number | null;
  modified?: string | null;
}

export interface FileListResponse {
  path: string;
  items: FileListItem[];
  status?: string;
  configured?: boolean;
  connected?: boolean;
  error?: string | null;
}

export interface FileUploadResponse {
  path: string;
  etag?: string | null;
  ocrId?: string | null;
  ocrProvider?: string | null;
  ocrStatus?: string | null;
  documentType?: string | null;
  paperlessId?: number | null;
  ocrTextPreview?: string | null;
  organized?: boolean;
}

export async function fetchFiles(path = ''): Promise<ApiResponse<FileListResponse>> {
  return get<FileListResponse>(`/v1/files/list${buildQuery({ path })}`, { suppressToast: true });
}

export async function uploadFile(
  path: string,
  file: File
): Promise<ApiResponse<FileUploadResponse>> {
  const form = new FormData();
  form.append('file', file);
  return post<FileUploadResponse>(`/v1/files/upload${buildQuery({ path })}`, form);
}

export interface FilesStatusResponse {
  configured: boolean;
  connected: boolean;
  status: string;
  error?: string | null;
}

export async function fetchFilesStatus(): Promise<ApiResponse<FilesStatusResponse>> {
  return get<FilesStatusResponse>('/v1/files/status', { suppressToast: true });
}

export function fileDownloadUrl(path: string): string {
  return `${API_BASE_URL}/v1/files/download${buildQuery({ path })}`;
}

export async function mkdirFile(path: string): Promise<ApiResponse<{ ok: boolean; path: string }>> {
  return post('/v1/files/mkdir', { path });
}

export async function moveFile(
  source: string,
  destination: string
): Promise<ApiResponse<{ ok: boolean; source: string; destination: string }>> {
  return post('/v1/files/move', { source, destination });
}

export async function deleteFile(path: string): Promise<ApiResponse<{ ok: boolean; path: string }>> {
  return post('/v1/files/delete', { path });
}

export async function organizeZipFile(
  file: File,
  dryRun = false
): Promise<ApiResponse<Record<string, unknown>>> {
  const zipBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const b64 = result.split(',')[1] ?? '';
      resolve(b64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  return post('/v1/files/organize-zip', { zipBase64, dryRun });
}

// Wallets / Crypto
export interface WalletBalancesResponse {
  status: string;
  balances: Record<string, unknown>[];
}

export interface WalletActivityResponse {
  status: string;
  data: Record<string, unknown>[];
  total: number;
}

export interface StageCryptoJournalEntryRequest {
  date?: string;
  asset: string;
  amount: string;
  usd_value?: string;
  description?: string;
  account_debit?: string;
  account_credit?: string;
}

export interface StageCryptoJournalEntryResponse {
  status: string;
  approval_id: string;
  entry: Record<string, unknown>;
}

export async function fetchWalletBalances(
  walletType = 'all'
): Promise<ApiResponse<WalletBalancesResponse>> {
  return get<WalletBalancesResponse>(`/v1/wallets/balances${buildQuery({ wallet_type: walletType })}`, {
    suppressToast: true,
  });
}

export async function fetchWalletActivity(
  walletType = 'all',
  limit = 50,
  offset = 0
): Promise<ApiResponse<WalletActivityResponse>> {
  return get<WalletActivityResponse>(
    `/v1/wallets/activity${buildQuery({ wallet_type: walletType, limit, offset })}`,
    { suppressToast: true }
  );
}

export async function stageCryptoJournalEntry(
  payload: StageCryptoJournalEntryRequest
): Promise<ApiResponse<StageCryptoJournalEntryResponse>> {
  return post<StageCryptoJournalEntryResponse>('/v1/crypto/journal-entries/stage', payload);
}

// Assets
export interface AssetRegisterResponse {
  data: Record<string, unknown>[];
}

export async function fetchAssetRegister(): Promise<ApiResponse<AssetRegisterResponse>> {
  return get<AssetRegisterResponse>('/v1/assets/register', { suppressToast: true });
}

// Tax
export interface TaxEstimateResponse {
  status: string;
  year: number;
  quarter: string;
  revenue?: string | null;
  expenses?: string | null;
  taxable_income?: string | null;
  estimated_tax?: string | null;
  effective_rate?: string | null;
  source?: string | null;
}

export interface StageTaxFilingRequest {
  year: number;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  form_type?: '1040es' | '1120es' | '941' | 'sales' | 'other';
  estimated_tax?: string;
}

export interface StageTaxFilingResponse {
  status: string;
  filing_id: string;
  approval_id: string;
  filing: Record<string, unknown>;
}

export interface TransmitTaxFilingResponse {
  status: string;
  filing_id: string;
  approval_id: string;
  message: string;
  filing: Record<string, unknown>;
}

export async function fetchTaxEstimate(
  year: number,
  quarter: string,
  overrides: { revenue?: string; expenses?: string } = {}
): Promise<ApiResponse<TaxEstimateResponse>> {
  return get<TaxEstimateResponse>(
    `/v1/tax/estimate${buildQuery({ year, quarter, revenue: overrides.revenue, expenses: overrides.expenses })}`,
    { suppressToast: true }
  );
}

export async function stageTaxFiling(
  payload: StageTaxFilingRequest
): Promise<ApiResponse<StageTaxFilingResponse>> {
  return post<StageTaxFilingResponse>('/v1/tax/filings/stage', payload);
}

export async function transmitTaxFiling(
  filingId: string,
  approvalId: string
): Promise<ApiResponse<TransmitTaxFilingResponse>> {
  return post<TransmitTaxFilingResponse>(
    `/v1/tax/filings/${encodeURIComponent(filingId)}/transmit`,
    { approval_id: approvalId }
  );
}

// Backups
export interface BackupTimerStatus {
  available?: boolean;
  timer_name?: string;
  active_state?: string;
  unit_file_state?: string;
  next_elapse?: string | null;
  last_trigger?: string | null;
}

export interface BackupComponentStatus {
  name: string;
  timer: BackupTimerStatus;
  log_path: string;
  last_success: string | null;
  last_error: string | null;
  reachable: boolean;
  log_tail: string[];
}

export interface BackupStatusResponse {
  status: string;
  timestamp: string;
  general: BackupComponentStatus;
  legal: BackupComponentStatus;
  targets: Record<string, unknown>;
}

export async function fetchBackupStatus(): Promise<ApiResponse<BackupStatusResponse>> {
  return get<BackupStatusResponse>('/v1/backups/status', { suppressToast: true });
}

// Webhooks
export interface WebhookEvent {
  id: number;
  request_id?: string | null;
  source: string;
  event_id?: string | null;
  idempotency_key: string;
  received_at: string;
  status: string;
  signature_status: string;
  payload_path?: string | null;
  payload_hash?: string | null;
  headers: Record<string, unknown>;
  metadata: Record<string, unknown>;
  error_message?: string | null;
  retry_count: number;
  processed_at?: string | null;
  created_at: string;
}

export interface WebhookListResponse {
  items: WebhookEvent[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface WebhookSource {
  source: string;
  header_name: string;
  algorithm: string;
  configured: boolean;
}

export interface WebhookRetryResponse {
  webhook_id: number;
  status: string;
  retry_count: number;
  adapter_result: Record<string, unknown>;
}

export async function fetchWebhooks(
  params: { page?: number; page_size?: number; source?: string; status?: string; q?: string } = {}
): Promise<ApiResponse<WebhookListResponse>> {
  return get<WebhookListResponse>(`/v1/webhooks${buildQuery({ ...params })}`, {
    suppressToast: true,
  });
}

export async function fetchWebhookSources(): Promise<ApiResponse<WebhookSource[]>> {
  return get<WebhookSource[]>('/v1/webhooks/sources', { suppressToast: true });
}

export async function fetchWebhook(id: number): Promise<ApiResponse<WebhookEvent>> {
  return get<WebhookEvent>(`/v1/webhooks/${id}`, { suppressToast: true });
}

export async function retryWebhook(id: number): Promise<ApiResponse<WebhookRetryResponse>> {
  return post<WebhookRetryResponse>(`/v1/webhooks/${id}/retry`);
}

// ---------------------------------------------------------------------------
// AI chat: attachments, streaming tool-calling loop, conversation persistence.
// (Appended; existing AI helpers above are left untouched.)
// ---------------------------------------------------------------------------

export interface EntityLearnedSummary {
  entityName?: string | null;
  entityType?: string | null;
  formationDate?: string | null;
  formationState?: string | null;
}

export interface AiAttachment {
  id: string;
  conversationId?: string | null;
  filename: string;
  contentType?: string | null;
  sizeBytes: number;
  status: string;
  summary: string;
  extractedChars: number;
  learned?: EntityLearnedSummary | null;
  ocrProvider?: string | null;
  ocrId?: string | null;
  documentType?: string | null;
  storageRef?: string | null;
  referenceId?: string | null;
  shortRef?: string | null;
  ocisPath?: string | null;
  ocisSpaceId?: string | null;
  onServer?: boolean;
  createdAt?: string | null;
  suggestedTool?: string | null;
  bytesAvailable?: boolean;
  unavailableReason?: string | null;
}

export interface AiAttachmentDetail extends AiAttachment {
  extractedText: string;
}

export interface ChatToolCallTrace {
  id: string;
  name: string;
  arguments?: unknown;
  result?: Record<string, unknown> | unknown[] | null;
  ok: boolean;
}

export interface AiToolChatResponse {
  content: string;
  conversationId: string;
  toolCalls: ChatToolCallTrace[];
  approvals: Array<Record<string, unknown>>;
}

export interface StoredChatMessage {
  id: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  createdAt?: string;
  toolCalls?: unknown;
  toolCallId?: string;
  name?: string;
}

export interface ConversationSummary {
  id: string;
  title: string;
  preview: string;
  pinned: boolean;
  archived: boolean;
  agentMode: boolean;
  template?: string | null;
  model?: string | null;
  provider?: string | null;
  messageCount: number;
  titleLocked: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StoredConversation extends ConversationSummary {
  messages: StoredChatMessage[];
  attachments: AiAttachment[];
}

export async function uploadChatAttachment(
  file: File,
  conversationId?: string
): Promise<ApiResponse<AiAttachment>> {
  const form = new FormData();
  form.append('file', file);
  if (conversationId) form.append('conversation_id', conversationId);
  return apiRequest<AiAttachment>('POST', '/v1/ai/chat/attachments', form, {
    suppressToast: false,
  });
}

export async function fetchConversation(
  conversationId: string
): Promise<ApiResponse<StoredConversation>> {
  return get<StoredConversation>(`/v1/ai/conversations/${encodeURIComponent(conversationId)}`, {
    suppressToast: true,
  });
}

export async function fetchConversations(params: {
  q?: string;
  archived?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<ApiResponse<{ items: ConversationSummary[]; total: number }>> {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.archived) query.set('archived', 'true');
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  const suffix = query.toString() ? `?${query}` : '';
  return get(`/v1/ai/conversations${suffix}`, { suppressToast: true });
}

export async function createConversation(body: {
  title?: string;
  agentMode?: boolean;
  template?: string;
} = {}): Promise<ApiResponse<ConversationSummary>> {
  return post('/v1/ai/conversations', body);
}

export async function updateConversation(
  conversationId: string,
  body: { title?: string; pinned?: boolean; archived?: boolean; agentMode?: boolean; template?: string }
): Promise<ApiResponse<ConversationSummary>> {
  return patch(`/v1/ai/conversations/${encodeURIComponent(conversationId)}`, body);
}

export async function deleteConversation(
  conversationId: string
): Promise<ApiResponse<{ ok: boolean; id: string }>> {
  return del(`/v1/ai/conversations/${encodeURIComponent(conversationId)}`);
}

export async function generateConversationTitle(
  conversationId: string
): Promise<ApiResponse<ConversationSummary>> {
  return post(`/v1/ai/conversations/${encodeURIComponent(conversationId)}/title`, {}, {
    suppressToast: true,
  });
}

export async function fetchAttachmentDetail(
  attachmentId: string
): Promise<ApiResponse<AiAttachmentDetail>> {
  return get(`/v1/ai/attachments/${encodeURIComponent(attachmentId)}`, { suppressToast: true });
}

export async function reprocessAttachment(
  attachmentId: string,
  provider?: string
): Promise<ApiResponse<AiAttachmentDetail & { ok: boolean; error?: string }>> {
  return post(`/v1/ai/attachments/${encodeURIComponent(attachmentId)}/reprocess`, { provider });
}

export async function deleteAttachment(
  attachmentId: string
): Promise<ApiResponse<{ ok: boolean; id: string }>> {
  return del(`/v1/ai/attachments/${encodeURIComponent(attachmentId)}`, { suppressToast: true });
}

export interface AiToolInfo {
  name: string;
  description: string;
  category: string;
  approvalRequired: boolean;
  riskLevel?: string | null;
  allowed: boolean;
  parameters: Record<string, unknown>;
}

export interface AiToolCatalog {
  ok: boolean;
  connected: boolean;
  total: number;
  categories: string[];
  allowlistActive: boolean;
  tools: AiToolInfo[];
  error?: string | null;
}

export async function fetchAiToolCatalog(refresh = false): Promise<ApiResponse<AiToolCatalog>> {
  return get(`/v1/ai/tools${refresh ? '?refresh=true' : ''}`, { suppressToast: true });
}

export interface AiStatus {
  configured: boolean;
  provider?: string | null;
  model?: string | null;
  providers: Array<{ name: string; model: string; active: boolean; configured?: boolean }>;
  mcpConnected: boolean;
  toolCount: number;
  maxToolIterations: number;
  ocrProvider?: string | null;
  ocrFallbackEnabled: boolean;
  attachmentMaxChars: number;
  memoryFacts: number;
  memoryDeadlines: number;
  conversationCount: number;
}

export async function fetchAiStatus(): Promise<ApiResponse<AiStatus>> {
  return get('/v1/ai/status', { suppressToast: true });
}

export interface EntityFact {
  id: number;
  category: string;
  factKey: string;
  value: unknown;
  confidence: number;
  rawSnippet?: string | null;
  sourceDocumentId?: string | null;
  sourceFileHash?: string | null;
  extractedAt?: string | null;
}

export async function fetchEntityFacts(): Promise<
  ApiResponse<{ ok: boolean; facts: EntityFact[]; summary: EntityLearnedSummary }>
> {
  return get('/v1/entity-memory/facts', { suppressToast: true });
}

export async function setEntityFact(body: {
  category: string;
  fact_key: string;
  value: unknown;
  confidence?: number;
}): Promise<ApiResponse<{ ok: boolean; fact: EntityFact }>> {
  return post('/v1/entity-memory/facts', body);
}

export async function deleteEntityMemorySource(
  sourceDocumentId: string
): Promise<ApiResponse<{ ok: boolean; deleted: string }>> {
  return del(`/v1/entity-memory/sources/${encodeURIComponent(sourceDocumentId)}`);
}

export interface AiChatStreamRequest {
  messages: ChatMessage[];
  template?: string;
  context?: Record<string, unknown>;
  attachment_ids?: string[];
  conversation_id?: string;
  thinking?: boolean;
  use_stored_history?: boolean;
  truncate_after_message_id?: string;
}

/**
 * Open a streaming, tool-calling chat request. Returns the raw fetch Response
 * whose body is a `text/event-stream` the caller reads incrementally. Throws on
 * network/HTTP failure so the caller can surface an error state.
 *
 * Retries 502/503/504/408/429 and network failures before the stream starts
 * (3 attempts, 500ms → 1s → 2s). Mid-stream failures are handled by the caller.
 */
export async function openAiChatStream(
  payload: AiChatStreamRequest,
  signal?: AbortSignal,
  opts?: { onRetry?: (info: RetryAttemptInfo) => void }
): Promise<Response> {
  const url = `${API_BASE_URL}/v1/ai/chat`;
  let response: Response;
  try {
    response = await fetchWithRetry(
      url,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...devAuthHeaders(),
        },
        body: JSON.stringify({
          ...payload,
          stream: true,
          use_tools: true,
          thinking: payload.thinking ?? false,
        }),
        signal,
      },
      {
        retries: 3,
        baseDelayMs: 500,
        onRetry: opts?.onRetry,
      }
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    const raw = err instanceof Error ? err.message : 'Network error';
    throw new Error(
      /failed to fetch|networkerror|load failed/i.test(raw)
        ? 'Network error — still unavailable after 3 attempts. Please try again in a moment.'
        : `${raw} — still unavailable after 3 attempts. Please try again in a moment.`
    );
  }
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    const detail =
      summarizeHttpErrorBody(response.status, text, response.statusText) ||
      `${response.status} ${response.statusText}`;
    const suffix = isRetryableHttpStatus(response.status)
      ? ' — still unavailable after 3 attempts. Please try again in a moment.'
      : '';
    throw new Error(`${detail}${suffix}`);
  }
  return response;
}

export type ChatStreamEvent =
  | {
      type: 'turn';
      conversationId: string;
      userMessageId: string | null;
      title: string;
    }
  | { type: 'start'; conversationId: string; toolCount: number }
  | { type: 'thinking_start'; intent?: string | null }
  | { type: 'thinking'; delta: string }
  | { type: 'thinking_done' }
  | { type: 'token'; delta: string }
  | { type: 'tool_call'; id: string; name: string; arguments: unknown }
  | { type: 'tool_result'; id: string; name: string; ok: boolean; result: Record<string, unknown> }
  | {
      type: 'approval_required';
      id: string;
      name: string;
      approvalId: string;
      approvalStatus: string;
      riskLevel: string;
      arguments: unknown;
      result: Record<string, unknown>;
    }
  | { type: 'warning'; message: string }
  | { type: 'ping' }
  | { type: 'error'; message: string }
  | {
      type: 'secure_form';
      id?: string;
      formId: string;
      title: string;
      fields: SecureFormField[];
      irsFormNumber?: string;
      fieldMapping?: Record<string, string>;
      shareUrl?: string;
      recipientName?: string;
      expiresAt?: string;
      purpose?: string;
      status: string;
    }
  | { type: 'done'; content: string; conversationId: string; thinking?: string | null };

export interface SecureFormField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'ssn' | 'ein' | 'address' | 'select';
  required?: boolean;
  sensitive?: boolean;
  options?: string[];
  description?: string;
}

export interface SecureFormStatus {
  form_id: string;
  title: string;
  fields: Record<string, boolean>;
  all_required_filled: boolean;
  recipient_name?: string | null;
  purpose?: string | null;
  submitted?: boolean;
  share_url?: string | null;
  expires_at?: string | null;
}

export interface PublicSecureForm {
  ok?: boolean;
  form_id: string;
  title: string;
  fields: SecureFormField[];
  recipient_name?: string | null;
  purpose?: string | null;
  expires_at?: string | null;
  submitted?: boolean;
  all_required_filled?: boolean;
  fields_status?: Record<string, boolean>;
  irs_form_number?: string | null;
}

export async function submitSecureForm(
  formId: string,
  values: Record<string, string>
): Promise<ApiResponse<{ ok: boolean; filled_keys: string[] }>> {
  return apiRequest(
    'PUT',
    `/v1/secure-vault/forms/${encodeURIComponent(formId)}/submit`,
    { values },
    { retries: 3, baseDelayMs: 500, suppressToast: true }
  );
}

export async function getSecureFormStatus(
  formId: string
): Promise<ApiResponse<SecureFormStatus>> {
  return apiRequest('GET', `/v1/secure-vault/forms/${encodeURIComponent(formId)}/status`);
}

export async function fillIrsFromSecureVault(
  formId: string,
  body: { form_number: string; field_mapping: Record<string, string>; year?: number }
): Promise<
  ApiResponse<{
    ok: boolean;
    download_url?: string;
    download_token?: string;
    error?: string;
    form_number?: string;
    year?: number;
  }>
> {
  return apiRequest('POST', `/v1/secure-vault/forms/${encodeURIComponent(formId)}/fill-irs`, body);
}

export async function fetchPublicSecureForm(
  token: string
): Promise<ApiResponse<PublicSecureForm>> {
  return get<PublicSecureForm>(`/v1/secure-vault/public/${encodeURIComponent(token)}`, {
    suppressToast: true,
  });
}

export async function submitPublicSecureForm(
  token: string,
  values: Record<string, string>
): Promise<
  ApiResponse<{
    ok: boolean;
    filled_keys: string[];
    form_id?: string;
    submitted?: boolean;
    all_required_filled?: boolean;
  }>
> {
  return put(`/v1/secure-vault/public/${encodeURIComponent(token)}/submit`, { values }, {
    suppressToast: true,
    retries: 3,
    baseDelayMs: 500,
  });
}

/**
 * Read a `text/event-stream` Response body and invoke `onEvent` for each parsed
 * `data:` JSON event until the stream ends or `[DONE]` is received.
 */
export async function consumeChatStream(
  response: Response,
  onEvent: (event: AgentChatStreamEvent) => void
): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flush = (raw: string) => {
    const line = raw.trim();
    if (!line.startsWith('data:')) return;
    const data = line.slice('data:'.length).trim();
    if (!data || data === '[DONE]') return;
    try {
      onEvent(JSON.parse(data) as AgentChatStreamEvent);
    } catch {
      // Ignore malformed partial chunks.
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of block.split('\n')) flush(line);
    }
  }
  if (buffer) for (const line of buffer.split('\n')) flush(line);
}

// ---------------------------------------------------------------------------
// Accounting agent orchestrator, notifications, file intake
// ---------------------------------------------------------------------------

export interface AgentTask {
  id: string;
  parentId?: string | null;
  type: string;
  status: string;
  goal?: string | null;
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
  pendingQuestion?: string | null;
  pendingAnswer?: string | null;
  error?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  children?: AgentTask[];
}

export interface OperatorNotification {
  id: string;
  title: string;
  body: string;
  steps: string[];
  priority: string;
  relatedApprovalId?: string | null;
  relatedTaskId?: string | null;
  acknowledged: boolean;
  acknowledgedAt?: string | null;
  createdAt?: string;
}

export interface FileIntakeResult {
  filename: string;
  category: string;
  destination: string;
  summary?: string;
  status: string;
  confidence?: number;
  classifyReason?: string;
}

export async function runAccountingAgent(goal: string): Promise<ApiResponse<{ taskId: string; status: string; summary?: string }>> {
  return post('/v1/agent/run', { goal });
}

export async function fetchAgentTasks(parentId?: string): Promise<ApiResponse<{ items: AgentTask[]; total: number }>> {
  const q = parentId ? `?parent_id=${encodeURIComponent(parentId)}` : '';
  return get(`/v1/agent/tasks${q}`, { suppressToast: true });
}

export async function fetchAgentTask(taskId: string): Promise<ApiResponse<AgentTask>> {
  return get(`/v1/agent/tasks/${encodeURIComponent(taskId)}`, { suppressToast: true });
}

export async function cancelAgentTask(taskId: string): Promise<ApiResponse<Record<string, unknown>>> {
  return post(`/v1/agent/tasks/${encodeURIComponent(taskId)}/cancel`, {});
}

export async function answerAgentQuestion(
  taskId: string,
  answer: string
): Promise<ApiResponse<Record<string, unknown>>> {
  return post('/v1/agent/answer', { taskId, answer });
}

export async function fetchNotifications(
  unreadOnly = false
): Promise<ApiResponse<{ items: OperatorNotification[]; total: number; unreadCount: number }>> {
  const q = unreadOnly ? '?unread_only=true' : '';
  return get(`/v1/notifications${q}`, { suppressToast: true });
}

export async function acknowledgeNotification(id: string): Promise<ApiResponse<{ ok: boolean; id: string }>> {
  return post(`/v1/notifications/${encodeURIComponent(id)}/ack`, {});
}

export async function intakeFiles(
  files: Array<{ filename: string; contentBase64: string; contentType?: string }>,
  dryRun = false,
  conversationId?: string | null
): Promise<ApiResponse<{ ok: boolean; count: number; results: FileIntakeResult[] }>> {
  return post('/v1/files/intake', {
    files,
    dryRun,
    ...(conversationId ? { conversationId } : {}),
  });
}

export type AgentChatStreamEvent =
  | ChatStreamEvent
  | { type: 'agent_task'; taskId: string; status: string; goal: string }
  | { type: 'agent_plan'; taskId: string; plan: Array<{ type: string; input?: Record<string, unknown> }> }
  | { type: 'agent_subtasks'; taskId: string; subtasks: unknown[] }
  | { type: 'agent_done'; taskId: string; summary: string };

export async function openAgentChatStream(
  payload: AiChatStreamRequest,
  signal?: AbortSignal,
  opts?: { onRetry?: (info: RetryAttemptInfo) => void }
): Promise<Response> {
  const url = `${API_BASE_URL}/v1/ai/chat`;
  let response: Response;
  try {
    response = await fetchWithRetry(
      url,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...devAuthHeaders(),
        },
        body: JSON.stringify({
          ...payload,
          stream: true,
          use_tools: true,
          agent_mode: true,
          thinking: payload.thinking ?? false,
        }),
        signal,
      },
      {
        retries: 3,
        baseDelayMs: 500,
        onRetry: opts?.onRetry,
      }
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    const raw = err instanceof Error ? err.message : 'Network error';
    throw new Error(
      /failed to fetch|networkerror|load failed/i.test(raw)
        ? 'Network error — still unavailable after 3 attempts. Please try again in a moment.'
        : `${raw} — still unavailable after 3 attempts. Please try again in a moment.`
    );
  }
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    const detail =
      summarizeHttpErrorBody(response.status, text, response.statusText) ||
      `${response.status} ${response.statusText}`;
    const suffix = isRetryableHttpStatus(response.status)
      ? ' — still unavailable after 3 attempts. Please try again in a moment.'
      : '';
    throw new Error(`${detail}${suffix}`);
  }
  return response;
}

export function getStatusColor(status: IntegrationStatus['status']): string {
  switch (status) {
    case 'ok':
      return 'bg-zt-primary';
    case 'warn':
      return 'bg-zt-warning';
    case 'error':
      return 'bg-zt-danger';
    default:
      return 'bg-zt-muted';
  }
}

export function getStatusLabel(status: IntegrationStatus['status']): string {
  switch (status) {
    case 'ok':
      return 'Healthy';
    case 'warn':
      return 'Warning';
    case 'error':
      return 'Error';
    default:
      return 'Unknown';
  }
}

// --- Public checkout (pay.zerotail.org) ---

export interface CheckoutMethod {
  id: string;
  label: string;
  description: string;
  configured: boolean;
  endpoint: string;
}

export interface CheckoutLineItem {
  id?: string;
  sku?: string | null;
  name: string;
  description?: string | null;
  quantity: number;
  unit_price: string;
  currency?: string;
  tax_rate?: number | null;
  image_url?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CheckoutIntent {
  status: string;
  token: string;
  payment_status: string;
  amount: string;
  currency: string;
  description?: string | null;
  invoice_ref?: string | null;
  return_url?: string | null;
  cancel_url?: string | null;
  allowed_methods?: string[] | null;
  pay_url: string;
  created_at: string;
  expires_at: string;
  line_items?: CheckoutLineItem[] | null;
  subtotal?: string | null;
  tax_total?: string | null;
  discount_total?: string | null;
  shipping_total?: string | null;
  discount_code?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  shipping_address?: Record<string, unknown> | null;
  billing_address?: Record<string, unknown> | null;
  monero_address?: string | null;
  monero_amount_xmr?: string | null;
  monero_payment_uri?: string | null;
  org?: 'llc' | 'labs' | string | null;
  org_branding?: {
    org?: string;
    legal_name?: string;
    short_name?: string;
    pay_label?: string;
    tagline?: string;
    receipt_prefix?: string;
    is_nonprofit?: boolean;
  } | null;
  receipt_id?: string | null;
  methods: CheckoutMethod[];
}

export interface PaymentIntentCreateBody {
  amount: string;
  currency: string;
  description?: string;
  invoice_ref?: string;
  methods?: string[];
  return_url?: string;
  cancel_url?: string;
  org?: 'llc' | 'labs';
  metadata?: Record<string, unknown>;
}

export interface PaymentIntentCreateResponse {
  status: string;
  token: string;
  pay_url: string;
  amount: string;
  currency: string;
  description?: string | null;
  invoice_ref?: string | null;
  expires_at: string;
}

export async function createPaymentIntent(
  body: PaymentIntentCreateBody,
): Promise<ApiResponse<PaymentIntentCreateResponse>> {
  return post<PaymentIntentCreateResponse>('/v1/checkout/intents', body);
}

export async function fetchCheckoutIntent(
  token: string,
): Promise<ApiResponse<CheckoutIntent>> {
  return get<CheckoutIntent>(`/v1/checkout/intents/${token}`, { suppressToast: true });
}

export interface MoneroPayResponse {
  status: string;
  address?: string;
  amount_xmr?: string;
  amount_fiat?: string;
  currency?: string;
  description?: string | null;
  invoice_ref?: string | null;
  payment_uri?: string;
  xmr_usd_rate?: string | null;
  error?: string;
}

export interface VerifyTxResponse {
  status: string;
  payment_status: string;
  message?: string | null;
  tx_hash?: string | null;
  confirmations?: number | null;
}

export interface CheckoutIntentStatus {
  status: string;
  token: string;
  payment_status: string;
  amount: string;
  currency: string;
  description?: string | null;
  invoice_ref?: string | null;
  expires_at: string;
  created_at: string;
}

export async function payCheckoutIntent(
  token: string,
  methodId: string,
): Promise<ApiResponse<Record<string, unknown>>> {
  return post<Record<string, unknown>>(
    `/v1/checkout/intents/${token}/pay/${methodId}`,
    {},
    { suppressToast: true },
  );
}

export async function capturePaypalCheckout(
  token: string,
  orderId: string,
): Promise<ApiResponse<Record<string, unknown>>> {
  const query = orderId ? `?order_id=${encodeURIComponent(orderId)}` : '';
  return post<Record<string, unknown>>(
    `/v1/checkout/intents/${token}/pay/paypal/capture${query}`,
    {},
    { suppressToast: true },
  );
}

export async function resetCheckoutIntent(
  token: string,
): Promise<ApiResponse<CheckoutIntent>> {
  return post<CheckoutIntent>(
    `/v1/checkout/intents/${token}/reset`,
    {},
    { suppressToast: true },
  );
}

export async function fetchCheckoutIntentStatus(
  token: string,
): Promise<ApiResponse<CheckoutIntentStatus>> {
  return get<CheckoutIntentStatus>(`/v1/checkout/intents/${token}/status`, {
    suppressToast: true,
  });
}

export async function verifyCheckoutTx(
  token: string,
  txHash?: string,
): Promise<ApiResponse<VerifyTxResponse>> {
  return post<VerifyTxResponse>(
    `/v1/checkout/intents/${token}/verify-tx`,
    txHash ? { tx_hash: txHash } : {},
    { suppressToast: true },
  );
}

export interface CheckoutMethodsResponse {
  status: string;
  methods: CheckoutMethod[];
}

export async function fetchCheckoutMethods(): Promise<ApiResponse<CheckoutMethodsResponse>> {
  return get<CheckoutMethodsResponse>('/v1/checkout/methods', { suppressToast: true });
}

// ---------------------------------------------------------------------------
// Generated documents (LaTeX → PDF) and code sandbox
// ---------------------------------------------------------------------------

export type DocumentTemplateType =
  | 'invoice'
  | 'receipt'
  | 'statement'
  | 'expense_report'
  | 'profit_loss'
  | 'letter'
  | 'report';

export interface DocumentTemplateInfo {
  type: string;
  label: string;
  description: string;
  required: string[];
  fields: Record<string, string>;
}

export interface GeneratedDocument {
  id: string;
  documentType: string;
  title: string;
  filename: string;
  sizeBytes: number;
  pageCount?: number | null;
  status: string;
  storageRef?: string | null;
  engine: string;
  conversationId?: string | null;
  taskId?: string | null;
  error?: string | null;
  createdBy: string;
  createdAt?: string | null;
  downloadUrl: string;
}

export interface DocumentGenerateRequest {
  documentType: DocumentTemplateType | string;
  data?: Record<string, unknown>;
  title?: string;
  engine?: 'pdflatex' | 'xelatex' | 'lualatex';
  conversationId?: string;
  taskId?: string;
  returnBase64?: boolean;
}

export interface SandboxStatus {
  enabled: boolean;
  backend: string;
  configuredBackend: string;
  dockerAvailable: boolean;
  resourceLimitsAvailable: boolean;
  timeoutSeconds: number;
  memoryMb: number;
  maxOutputChars: number;
  maxArtifactBytes: number;
  dockerImage: string;
  allowedModules: string[];
  networkAccess: boolean;
  requiresApproval: boolean;
}

export async function fetchSandboxStatus(): Promise<ApiResponse<SandboxStatus>> {
  return get('/v1/sandbox/status', { suppressToast: true });
}

export interface SandboxExecuteRequest {
  code: string;
  timeoutSeconds?: number;
  files?: Record<string, string>;
  conversationId?: string;
}

export interface SandboxExecuteResult {
  ok: boolean;
  backend: string;
  stdout?: string;
  stderr?: string;
  result?: unknown;
  error?: string | null;
  errorType?: string | null;
  traceback?: string | null;
  exitCode?: number | null;
  durationMs?: number;
  timedOut?: boolean;
  artifacts?: Array<{
    name: string;
    sizeBytes: number;
    contentBase64?: string | null;
    truncated?: boolean;
  }>;
  limitsApplied?: string[];
}

export async function listDocumentTemplates(): Promise<
  ApiResponse<{ templates: DocumentTemplateInfo[]; engines: string[]; configured: boolean }>
> {
  return get('/v1/documents/templates', { suppressToast: true });
}

export async function generateDocument(
  body: DocumentGenerateRequest
): Promise<ApiResponse<{ ok: boolean; document?: GeneratedDocument; pdfBase64?: string | null; error?: string | null }>> {
  return post('/v1/documents/generate', body);
}

export async function listGeneratedDocuments(params?: {
  q?: string;
  documentType?: string;
  conversationId?: string;
  mine?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<{ items: GeneratedDocument[]; total: number }>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.documentType) qs.set('documentType', params.documentType);
  if (params?.conversationId) qs.set('conversationId', params.conversationId);
  if (params?.mine) qs.set('mine', 'true');
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return get(`/v1/documents/generated${q ? `?${q}` : ''}`, { suppressToast: true });
}

export async function getGeneratedDocument(
  documentId: string
): Promise<ApiResponse<GeneratedDocument & { latexSource?: string; params?: Record<string, unknown> }>> {
  return get(`/v1/documents/generated/${encodeURIComponent(documentId)}`, { suppressToast: true });
}

export async function deleteGeneratedDocument(
  documentId: string
): Promise<ApiResponse<{ ok: boolean; id: string }>> {
  return del(`/v1/documents/generated/${encodeURIComponent(documentId)}`);
}

/** Absolute-or-relative download URL for a generated PDF (auth via cookie / API key). */
export function generatedDocumentDownloadUrl(documentId: string, inline = false): string {
  const base = `${API_BASE_URL}/v1/documents/generated/${encodeURIComponent(documentId)}/download`;
  return inline ? `${base}?inline=true` : base;
}

const GENERATED_DOC_DOWNLOAD_RE =
  /(?:^|\/)(?:api\/)?v1\/documents\/generated\/([^/?#]+)\/download(?:\?|$)/i;

/** True when href points at a generated-PDF download route (relative or absolute). */
export function isGeneratedDocumentDownloadUrl(href: string): boolean {
  try {
    const path = href.startsWith('http://') || href.startsWith('https://')
      ? new URL(href).pathname
      : href.split('?')[0];
    return GENERATED_DOC_DOWNLOAD_RE.test(path);
  } catch {
    return GENERATED_DOC_DOWNLOAD_RE.test(href);
  }
}

export function parseGeneratedDocumentId(href: string): string | null {
  try {
    const path = href.startsWith('http://') || href.startsWith('https://')
      ? new URL(href).pathname
      : href.split('?')[0];
    const m = path.match(/\/documents\/generated\/([^/]+)\/download\/?$/i);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/** Resolve a same-origin or API-relative href for authenticated fetch. */
export function resolveApiHref(href: string): string {
  if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('blob:')) {
    return href;
  }
  if (href.startsWith('/')) return href;
  return `${API_BASE_URL}/${href.replace(/^\//, '')}`;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      return utf[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * Fetch an authenticated API URL (session cookie + optional dev API key) and
 * trigger a browser file download via object URL. Prefer this over bare
 * `<a target=_blank>` for generated PDFs so auth always goes with the request.
 */
export async function downloadAuthenticatedUrl(
  href: string,
  filenameHint?: string
): Promise<{ ok: boolean; error?: string }> {
  const url = resolveApiHref(href);
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/pdf,*/*',
        ...devAuthHeaders(),
      },
    });
    if (!response.ok) {
      const message =
        response.status === 401
          ? 'Authentication required to download this file'
          : `Download failed (${response.status})`;
      toast.error(message);
      return { ok: false, error: message };
    }
    const blob = await response.blob();
    const filename =
      filenameHint ||
      filenameFromContentDisposition(response.headers.get('Content-Disposition')) ||
      'download.pdf';
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Download failed';
    toast.error(message);
    return { ok: false, error: message };
  }
}

export async function downloadGeneratedDocument(
  documentId: string,
  filenameHint?: string
): Promise<{ ok: boolean; error?: string }> {
  return downloadAuthenticatedUrl(generatedDocumentDownloadUrl(documentId), filenameHint);
}

export async function sandboxStatus(): Promise<ApiResponse<SandboxStatus>> {
  return get('/v1/sandbox/status', { suppressToast: true });
}

export async function executeSandbox(
  body: SandboxExecuteRequest
): Promise<ApiResponse<SandboxExecuteResult>> {
  return post('/v1/sandbox/execute', body);
}
